/**
 * Advise-first strategy view — same model as the CA PDF, rendered in the UI.
 */
import { Check, Target } from 'lucide-react';
import { Spinner } from '@/components/spinner';
import { Badge } from '@/components/ui/badge';
import {
  Section,
  SectionDescription,
  SectionHeader,
  SectionTitle,
} from '@/components/ui/section';
import { Surface } from '@/components/ui/surface';
import { cn } from '@/lib/cn';
import type { CompetitorAnalysisInsights } from '../insights-types';

export function StrategyPanel({
  data,
  loading,
  error,
}: {
  data: CompetitorAnalysisInsights | undefined;
  loading: boolean;
  error: Error | null;
}): JSX.Element {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <Section>
        <p className="text-sm text-destructive">
          Couldn’t load strategy insights. Try refreshing, or export the PDF.
        </p>
      </Section>
    );
  }
  if (!data?.can_beat_them) return <></>;

  const beat = data.can_beat_them;
  const d = (beat.difficulty ?? '').toUpperCase();
  const difficultyTone =
    d === 'LOW' ? 'success' : d === 'HIGH' ? 'danger' : 'warning';

  return (
    <div className="space-y-4">
      <Section>
        <SectionHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <SectionTitle>Can I beat them?</SectionTitle>
              <SectionDescription>{data.overview}</SectionDescription>
            </div>
            <DifficultyBadge level={beat.difficulty} tone={difficultyTone} />
          </div>
        </SectionHeader>
        <ul className="space-y-2">
          {(beat.why_checks ?? []).map((c) => (
            <li key={c} className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          {beat.conclusion}
        </p>
        <div className="mt-4 grid gap-2 sm:grid-cols-5">
          {(
            [
              ['Research', beat.effort?.research],
              ['Writing', beat.effort?.writing],
              ['Graphics', beat.effort?.graphics],
              ['Review', beat.effort?.review],
              ['Total', beat.effort?.total],
            ] as const
          ).map(([label, value]) => (
            <Surface key={label} tone="muted" className="p-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {label}
              </div>
              <div className="mt-1 text-sm font-semibold">{value ?? '—'}</div>
            </Surface>
          ))}
        </div>
      </Section>

      {data.strategy ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Strategy summary
              </SectionTitle>
            </div>
          </SectionHeader>
          <div className="grid gap-4 lg:grid-cols-3">
            <StrategyColumn title="Why they rank" items={data.strategy.why_they_rank ?? []} />
            <StrategyColumn title="What they miss" items={data.strategy.what_they_miss ?? []} />
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Your biggest advantage
              </div>
              <Surface tone="muted" className="border-l-4 border-l-primary p-3">
                <p className="text-sm leading-relaxed">{data.strategy.your_advantage}</p>
              </Surface>
            </div>
          </div>
        </Section>
      ) : null}

      {(data.competitor_table ?? []).length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Why they rank / Weakness</SectionTitle>
            </div>
          </SectionHeader>
          <SimpleTable
            headers={['Competitor', 'Why they rank', 'Weakness']}
            rows={data.competitor_table.map((r) => [
              r.competitor,
              r.why_they_rank,
              r.weakness,
            ])}
          />
        </Section>
      ) : null}

      {(data.add_this ?? []).length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>What to add</SectionTitle>
              <SectionDescription>
                Assets and sections that close the biggest gaps.
              </SectionDescription>
            </div>
          </SectionHeader>
          <SimpleTable
            headers={['Add this', 'Why', 'SEO impact']}
            rows={data.add_this.map((r) => [r.add_this, r.why, r.seo_impact])}
          />
        </Section>
      ) : null}

      {(data.important_entities ?? []).length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Mention these naturally</SectionTitle>
            </div>
          </SectionHeader>
          <SimpleTable
            headers={['Entity', 'Reason']}
            rows={data.important_entities.map((e) => [e.name, e.reason])}
          />
        </Section>
      ) : null}

      {data.serp_format_map ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>SERP format map</SectionTitle>
              <SectionDescription>
                Which formats appear across the ranking pages.
              </SectionDescription>
            </div>
          </SectionHeader>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Page</th>
                  {data.serp_format_map.formats.map((f) => (
                    <th key={f} className="px-2 py-2 font-medium">
                      {f}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.serp_format_map.rows.map((row) => (
                  <tr key={row.label} className="border-b border-border/40">
                    <td className="py-2 pr-3 text-xs font-medium">{row.label}</td>
                    {row.cells.map((on, i) => (
                      <td key={i} className="px-2 py-2 text-center">
                        {on ? (
                          <Check className="mx-auto h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <span className="text-muted-foreground/40">·</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {(data.content_targets ?? []).length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Content targets</SectionTitle>
            </div>
          </SectionHeader>
          <SimpleTable
            headers={['Metric', 'Competitor avg', 'Recommendation', 'Reason']}
            rows={data.content_targets.map((t) => [
              t.metric,
              t.competitor_avg,
              t.recommendation,
              t.reason,
            ])}
          />
        </Section>
      ) : null}

      {data.differentiation ? (
        <Section>
          <SectionHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SectionTitle>Differentiation</SectionTitle>
              <Badge variant="outline">{data.differentiation.level}</Badge>
            </div>
          </SectionHeader>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {data.differentiation.reason}
          </p>
          {(data.differentiation.to_improve ?? []).length > 0 ? (
            <div className="mt-3">
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                To improve
              </div>
              <ul className="space-y-1.5">
                {data.differentiation.to_improve.map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Section>
      ) : null}

      {(data.must_cover_outline ?? []).length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Must-cover sections</SectionTitle>
            </div>
          </SectionHeader>
          <SimpleTable
            headers={['Section', 'Goal', 'Reason']}
            rows={data.must_cover_outline.map((s) => [s.section, s.goal, s.reason])}
          />
        </Section>
      ) : null}

      {data.final_recommendation ? (
        <Section>
          <SectionHeader>
            <div>
              <SectionTitle>Final recommendation</SectionTitle>
              <SectionDescription>{data.final_recommendation.summary}</SectionDescription>
            </div>
          </SectionHeader>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(
              [
                ['Words', data.final_recommendation.words],
                ['H2s', data.final_recommendation.h2_count],
                ['Tables', data.final_recommendation.tables],
                ['Videos', data.final_recommendation.videos],
                ['FAQs', data.final_recommendation.faqs],
                ['Case study', data.final_recommendation.case_study],
                ['Images', data.final_recommendation.images],
                ['Internal links', data.final_recommendation.internal_links],
                ['External links', data.final_recommendation.external_links],
              ] as const
            ).map(([label, value]) => (
              <Surface key={label} tone="muted" className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {label}
                </div>
                <div className="mt-1 text-sm font-semibold">{value}</div>
              </Surface>
            ))}
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Tone: {data.final_recommendation.tone} · Audience:{' '}
            {data.final_recommendation.audience}
          </p>
        </Section>
      ) : null}

      <Section tone="muted">
        <SectionTitle>Next step</SectionTitle>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Open a Content Brief for this keyword to get the writer execution plan —
          section briefs, AI prompt, keyword placement, and checklist.
        </p>
      </Section>
    </div>
  );
}

function DifficultyBadge({
  level,
  tone,
}: {
  level: string;
  tone: 'success' | 'warning' | 'danger';
}): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold',
        tone === 'success' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
        tone === 'warning' && 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
        tone === 'danger' && 'bg-destructive/10 text-destructive',
      )}
    >
      {(level ?? '').toUpperCase()}
    </span>
  );
}

function StrategyColumn({
  title,
  items,
}: {
  title: string;
  items: string[];
}): JSX.Element {
  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="text-sm leading-relaxed text-foreground/90">
            · {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SimpleTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-left text-sm">
        <thead>
          <tr className="border-b border-border/60 text-xs text-muted-foreground">
            {headers.map((h) => (
              <th key={h} className="py-2 pr-3 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/40 align-top">
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={cn(
                    'py-2.5 pr-3 leading-relaxed',
                    j === 0 && 'font-medium',
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
